import React, { useEffect } from 'react';
import { X, FileText, ShieldAlert, Scale, Cookie, ShieldCheck, Database, Landmark } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export type LegalDocType = 
  | 'master-terms' 
  | 'ma-protocol' 
  | 'ai-compliance' 
  | 'privacy-protocol' 
  | 'cookie-policy' 
  | 'arbitration-clauses' 
  | null;

interface LegalOverlayProps {
  docType: LegalDocType;
  onClose: () => void;
}

const DOCUMENTS = {
  'master-terms': {
    title: 'MASTER TERMS • VALUATION DISCLAIMER & SYNDICATE PRIVACY PROTOCOL (2026)',
    content: (
      <div className="space-y-6 text-[13px] leading-relaxed font-light text-[#141416]/70">
        <div className="text-[#141416] pb-3 border-b border-white/10">
          <strong className="text-[#B45309] block text-sm font-mono uppercase mb-1">
            РЕГЛАМЕНТ ИСПОЛЬЗОВАНИЯ ПЛАТФОРМЫ, ИНФОРМАЦИОННАЯ ПОЛИТИКА И ДИСКЛЕЙМЕР
          </strong>
          <span className="font-mono text-[11px] text-[#141416]/50 block">
            г. Москва · Редакция от 2026 года · Официальный ресурс: https://structurepartners.ru<br/>
            Управляющий оператор платформы: ООО «СТРУКТУРА И ПАРТНЕРЫ», ОГРН 1247700891240, ИНН 9704238910
          </span>
        </div>
        
        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">1. ОБЩИЕ ПОЛОЖЕНИЯ И АКЦЕПТ ОФЕРТЫ</strong>
          <p>
            1.1. Настоящий документ представляет собой официальное публичное предложение (публичную оферту в соответствии с п. 2 ст. 437 Гражданского кодекса РФ) 
            Оператора, действующего в интересах партнерского пула консультантов инвестиционного бутика «СТРУКТУРА & ПАРТНЕРЫ» (далее — «Компания»).
          </p>
          <p className="mt-2">
            1.2. Документ регулирует порядок доступа к Сайту, использование расчетно-аналитических терминалов, математических моделей предварительной 
            оценки (Indicative Valuation Engine), базы аналитических меморандумов и порядок первичного обращения продавцов (Sell-Side) и покупателей (Buy-Side) медицинских активов.
          </p>
          <p className="mt-2">
            1.3. Безоговорочный акцепт оферты (ст. 438 ГК РФ): Начало использования Пользователем любых сервисов Сайта, просмотр страниц, запуск интерактивных 
            калькуляторов либо отправка контактных данных через защищенные шлюзы означает полное и безоговорочное согласие со всеми положениями настоящего Регламента.
          </p>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            2. ОТГРАНИЧЕНИЕ ОТ ИНВЕСТИЦИОННЫХ РЕКОМЕНДАЦИЙ (ФЗ № 39-ФЗ)
          </strong>
          <p>
            2.1. Информация, аналитические материалы, мультипликаторы (EV/EBITDA, EV/S), индикативные расчеты и иные сведения, размещенные на настоящем Сайте, 
            носят исключительно информационно-справочный и аналитический характер, отражают экспертное суждение специалистов Компании и не являются индивидуальной 
            инвестиционной рекомендацией в понимании Федерального закона от 22.04.1996 № 39-ФЗ «О рынке ценных бумаг».
          </p>
          <p className="mt-2">
            2.2. Компания не осуществляет деятельность по инвестиционному консультированию, не входит в Единый реестр инвестиционных советников Банка России 
            и не совершает сделок с ценными бумагами на организованных торгах. Предоставление информации о возможности приобретения долей в уставном капитале ООО, 
            акций непубличных обществ или имущественных комплексов осуществляется исключительно в рамках оказания консультационных услуг по корпоративному 
            финансированию и сопровождению сделок слияний и поглощений (M&A).
          </p>
          <p className="mt-2">
            2.3. Пользователи Сайта и потенциальные инвесторы обязаны самостоятельно проводить юридическую, финансовую, налоговую и медицинско-лицензионную 
            проверку объектов инвестирования (Due Diligence) и принимать решения о совершении сделок на основе собственных суждений и заключений привлеченных независимых экспертов.
          </p>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            3. ПРАВОВОЙ СТАТУС КАЛЬКУЛЯТОРОВ И AI/ML-МОДЕЛЕЙ (ФЗ № 135-ФЗ)
          </strong>
          <p>
            3.1. Расчетные алгоритмы, интерактивные калькуляторы и терминалы, размещенные на Сайте, представляют собой математические модели предварительной 
            индикативной оценки (Indicative Valuation Model). Результаты их применения не являются отчетом об оценке объекта оценки в понимании Федерального закона 
            от 29.07.1998 № 135-ФЗ «Об оценочной деятельности в Российской Федерации» и не могут быть использованы в качестве официального документа при 
            разрешении споров в суде, представлении в государственные органы или кредитные организации.
          </p>
          <p className="mt-2">
            3.2. Отдельные автоматизированные сервисы Сайта используют алгоритмы машинного обучения и искусственного интеллекта (AI/ML) для первичного анализа 
            и структурирования вводных данных. Сгенерированные алгоритмами заключения являются синтетическими аналитическими суждениями, носят вспомогательный 
            характер и подлежат обязательной верификации профильным M&A-аналитиком Компании в рамках проведения процедуры предпродажного аудита (Vendor Due Diligence, VDD). 
            Компания не несет ответственности за любые прямые или косвенные убытки, возникшие в результате использования автоматизированных расчетов Сайта.
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">
            4. РЕЖИМ КОММЕРЧЕСКОЙ ТАЙНЫ И СИСТЕМА «СЛЕПЫХ ТИЗЕРОВ» (ФЗ № 98-ФЗ)
          </strong>
          <p>
            4.1. Компания обеспечивает режим конфиденциальности сведений о продавцах, покупателях и параметрах объектов сделок в соответствии с требованиями 
            Федерального закона от 29.07.2004 № 98-ФЗ «О коммерческой тайне».
          </p>
          <p className="mt-2">
            4.2. Первичная информация о продаваемых медицинских и стоматологических активах публично размещается на Сайте исключительно в форме обезличенных 
            информационных материалов («Blind Teasers»). Опубликованные показатели не содержат фирменного наименования, коммерческого обозначения (бренда), 
            точного адреса местонахождения и иных данных, позволяющих идентифицировать правообладателя актива. Допускается раскрытие масштаба (количество кресел/коек), 
            федерального округа и агрегированных финансовых показателей без раскрытия наименования юрлица и персональных данных учредителей.
          </p>
          <p className="mt-2">
            4.3. Предоставление расширенного информационного меморандума (Information Memorandum), доступа к виртуальной комнате данных (Virtual Data Room, VDR) 
            и раскрытие идентифицирующих сведений об объекте осуществляются строго после прохождения процедуры верификации статуса покупателя (KYC) и подписания 
            между Сторонами соглашения о конфиденциальности (Non-Disclosure Agreement, NDA).
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">
            5. ПРИНЦИПЫ СТРУКТУРИРОВАНИЯ M&A-СДЕЛОК И ТРАНЗАКЦИОННЫЙ ЦИКЛ
          </strong>
          <p>
            5.1. Все сделки структурируются на основе доказательного моста нормализованной операционной прибыли (Normalized EBITDA), очищенной от личных расходов 
            фаундера, нерыночных договоров аренды и разовых списаний (Add-backs).
          </p>
          <p className="mt-2">
            5.2. Средний отраслевой транзакционный цикл от выдачи мандата до закрытия сделки (Closing) составляет 4–7 месяцев при условии готовности актива 
            к процедуре Vendor Due Diligence. Компания не предоставляет юридически обязывающих гарантий закрытия сделки в фиксированный срок, поскольку финальное 
            решение принимается независимыми сторонами в условиях рыночной конъюнктуры.
          </p>
          <p className="mt-2">
            5.3. Историческая целевая внутренняя норма доходности (IRR) реализуемых стратегий консолидации составляет 24–32% и не является гарантией будущей доходности.
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">
            6. ОБРАБОТКА ДАННЫХ И ЛОКАЛИЗАЦИЯ ИНФРАСТРУКТУРЫ (ФЗ № 152-ФЗ)
          </strong>
          <p>
            6.1. Сайт использует файлы cookie и технические средства веб-аналитики (включая сервис «Яндекс Метрика», счетчик № 112544173) для обеспечения 
            корректной работы сервисов, авторизации в защищенных разделах VDR и проведения статистических исследований. Сетевые идентификаторы обрабатываются 
            в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных».
          </p>
          <p className="mt-2">
            6.2. Локализация баз данных: Сбор, запись, систематизация, накопление, хранение и извлечение персональных данных граждан РФ осуществляются 
            с использованием баз данных, находящихся исключительно на территории Российской Федерации (ч. 5 ст. 18 ФЗ № 152-ФЗ).
          </p>
          <p className="mt-2">
            6.3. Оператор осуществляет обработку персональных данных после подачи установленного законом уведомления в Федеральную службу по надзору в сфере связи, 
            информационных технологий и массовых коммуникаций (Роскомнадзор) в соответствии со ст. 22 ФЗ № 152-ФЗ.
          </p>
          <p className="mt-2">
            6.4. В случае выявления фактов неправомерной или случайной передачи (предоставления, распространения, доступа) персональных данных Оператор 
            уведомляет Роскомнадзор в течение 24 часов о произошедшем инциденте и в течение 72 часов о результатах внутреннего расследования (ч. 3.1 ст. 21 ФЗ № 152-ФЗ).
          </p>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            7. ИНСТИТУЦИОНАЛЬНЫЙ АРБИТРАЖ (МКАС ПРИ ТПП РФ И РАЦ)
          </strong>
          <p>
            7.1. Все споры, разногласия или требования, возникающие из настоящего Регламента, Соглашений о конфиденциальности (NDA), Договоров об оказании 
            консультационных услуг или в связи с ними, в том числе касающиеся их исполнения, нарушения, прекращения или недействительности, подлежат передаче 
            на разрешение в Международный коммерческий арбитражный суд при Торгово-промышленной палате Российской Федерации (МКАС при ТПП РФ) в соответствии 
            с его применимыми регламентами. Арбитражное разбирательство проводится в г. Москве на русском языке. Решение арбитража является окончательным 
            и обязательным для обеих Сторон.
          </p>
          <p className="mt-2">
            7.2. В качестве альтернативного третейского института по соглашению Сторон в договоре мандата может быть определен Российский Арбитражный Центр (РАЦ) 
            при АНО «Институт современного арбитража» в соответствии с его Правилами арбитража (г. Москва, разбирательство на русском языке).
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">8. РЕКВИЗИТЫ ОПЕРАТОРА ПЛАТФОРМЫ</strong>
          <p className="font-mono text-xs text-[#141416]/60">
            ООО «СТРУКТУРА И ПАРТНЕРЫ» (M&A Advisory)<br/>
            ОГРН: 1247700891240 | ИНН: 9704238910<br/>
            Юридический адрес: г. Москва, набережная Тараса Шевченко<br/>
            Комплаенс-канал: a.kiselev@structurepartners.ru | Telegram: @structure_partners_bot
          </p>
        </div>
      </div>
    )
  },

  'ma-protocol': {
    title: 'M&A MANDATE PROTOCOL • ARBITRATION RULES (ICAC / РАЦ 2026)',
    content: (
      <div className="space-y-6 text-[13px] leading-relaxed font-light text-[#141416]/70">
        <div className="text-[#141416] pb-3 border-b border-[#EAE5DE]">
          <strong className="text-[#8C6D37] block text-sm font-mono uppercase mb-1 font-bold">
            РЕГЛАМЕНТ СОПРОВОЖДЕНИЯ M&A-МАНДАТОВ И СТАНДАРТЫ СДЕЛОК В ЗДРАВООХРАНЕНИИ
          </strong>
          <span className="font-mono text-[11px] text-[#141416]/50 block">
            Институциональные стандарты защиты капитала продавца и правила третейского разбирательства
          </span>
        </div>
        
        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">1. ПРИНЦИПЫ ВЕДЕНИЯ M&A-МАНДАТОВ</strong>
          <p>
            1.1. Компания сопровождает сделки консолидации, полной продажи бизнеса (100% Cash-Out), стратегических слияний и привлечения капитала фондов Private Equity.
          </p>
          <p className="mt-2">
            1.2. Оценка стоимости предприятия (Enterprise Value, EV) опирается на формулу:
            <br/>
            <span className="font-mono text-[#8C6D37] bg-white border border-[#EAE5DE] px-2.5 py-1.5 rounded block my-1 shadow-2xs">
              EV = EBITDA_norm × M_sector − Net Debt
            </span>
            где EBITDA_norm — операционная прибыль, нормализованная по стандартам QoE (Quality of Earnings), M_sector — медианный отраслевой мультипликатор, Net Debt — чистый финансовый долг компании с учетом отложенных обязательств по авансам пациентов (Cost-to-Complete).
          </p>
          <p className="mt-2">
            1.3. Транзакционный цикл: Средний отраслевой транзакционный цикл от выдачи мандата до закрытия сделки составляет 4–7 месяцев при условии готовности VDD.
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">2. ИСКЛЮЧЕНИЕ КОНФЛИКТА ИНТЕРЕСОВ</strong>
          <p>
            2.1. Компания придерживается строгого принципа разделения сторон («Chinese Wall»): советник не представляет интересы продавца и покупателя 
            в одной сделке без предварительного письменного согласия обеих сторон.
          </p>
          <p className="mt-2">
            2.2. Предоставление информации органам управления и инвесторам осуществляется строго в порядке, предусмотренном Федеральным законом «Об обществах 
            с ограниченной ответственностью», уставом общества и двусторонними соглашениями о неразглашении конфиденциальной информации (NDA).
          </p>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            3. ТРЕТЕЙСКАЯ АРБИТРАЖНАЯ ОГОВОРКА (МКАС ПРИ ТПП РФ / РАЦ)
          </strong>
          <p className="font-medium text-[#141416]/90">
            Вариант 1 (Международный коммерческий арбитражный суд при Торгово-промышленной палате РФ):
          </p>
          <p className="font-mono text-xs text-[#141416]/80 bg-[#FAF8F5] border border-[#EAE5DE] p-3 rounded mt-1 select-all">
            «Все споры, разногласия или требования, возникающие из настоящего Соглашения, Соглашений о конфиденциальности (NDA), 
            Договоров об оказании консультационных услуг или в связи с ними, в том числе касающиеся их исполнения, нарушения, прекращения 
            или недействительности, подлежат передаче на разрешение в Международный коммерческий арбитражный суд при Торгово-промышленной палате 
            Российской Федерации в соответствии с его применимыми регламентами. Арбитражное разбирательство проводится в г. Москве на русском языке. 
            Решение арбитража является окончательным и обязательным для обеих Сторон.»
          </p>
          <p className="font-medium text-[#141416]/90 mt-3">
            Вариант 2 (Российский Арбитражный Центр — РАЦ):
          </p>
          <p className="font-mono text-xs text-[#141416]/80 bg-[#FAF8F5] border border-[#EAE5DE] p-3 rounded mt-1 select-all">
            «Все споры, возникающие из настоящего Договора (Соглашения) или в связи с ним, в том числе касающиеся его заключения, 
            изменения, исполнения, нарушения, расторжения или недействительности, подлежат разрешению в арбитраже (третейском разбирательстве), 
            администрируемом Российским Арбитражным Центром при АНО «Институт современного арбитража» в соответствии с Правилами арбитража. 
            Местом арбитража является г. Москва. Язык арбитражного разбирательства — русский. Решение арбитража является окончательным для Сторон.»
          </p>
        </div>
      </div>
    )
  },

  'ai-compliance': {
    title: 'REGULATORY COMPLIANCE • 39-ФЗ • 135-ФЗ • AI/ML DISCLAIMER',
    content: (
      <div className="space-y-6 text-[13px] leading-relaxed font-light text-[#141416]/70">
        <div className="text-[#141416] pb-3 border-b border-[#EAE5DE]">
          <strong className="text-[#8C6D37] block text-sm font-mono uppercase mb-1 font-bold">
            СТАТУС ИНТЕРАКТИВНЫХ МОДЕЛЕЙ И ОТГРАНИЧЕНИЕ ОТ ИНВЕСТИЦИОННЫХ РЕКОМЕНДАЦИЙ
          </strong>
          <span className="font-mono text-[11px] text-[#141416]/50 block">
            Нормативное соответствие Федеральным законам № 39-ФЗ, № 135-ФЗ и протоколам AI-валидации
          </span>
        </div>
        
        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            СТАТЬЯ 1. ОТГРАНИЧЕНИЕ ОТ ИНВЕСТИЦИОННЫХ РЕКОМЕНДАЦИЙ (ФЗ № 39-ФЗ)
          </strong>
          <p>
            1.1. Информация, аналитические материалы, мультипликаторы (EV/EBITDA, EV/S), индикативные расчеты и иные сведения, размещенные на настоящем Сайте, 
            носят исключительно информационно-справочный и аналитический характер, отражают экспертное суждение специалистов Компании и не являются индивидуальной 
            инвестиционной рекомендацией в понимании Федерального закона от 22.04.1996 № 39-ФЗ «О рынке ценных бумаг».
          </p>
          <p className="mt-2">
            1.2. Компания не осуществляет деятельность по инвестиционному консультированию, не входит в Единый реестр инвестиционных советников Банка России 
            и не совершает сделок с ценными бумагами на организованных торгах. Предоставление информации о возможности приобретения долей в уставном капитале ООО, 
            акций непубличных обществ или имущественных комплексов осуществляется в рамках оказания консультационных услуг по корпоративному финансированию 
            и сопровождению сделок слияний и поглощений (M&A).
          </p>
          <p className="mt-2">
            1.3. Пользователи Сайта и потенциальные инвесторы обязаны самостоятельно проводить юридическую, финансовую, налоговую и медицинско-лицензионную 
            проверку объектов инвестирования (Due Diligence) и принимать решения о совершении сделок на основе собственных суждений и заключений привлеченных независимых экспертов.
          </p>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            СТАТЬЯ 2. ОГРАНИЧЕНИЕ ОТВЕТСТВЕННОСТИ КАЛЬКУЛЯТОРОВ И AI/ML-МОДЕЛЕЙ (ФЗ № 135-ФЗ)
          </strong>
          <p>
            2.1. Расчетные алгоритмы, интерактивные калькуляторы и терминалы, размещенные на Сайте, представляют собой математические модели предварительной 
            индикативной оценки (Indicative Valuation Model). Результаты их применения не являются отчетом об оценке объекта оценки в понимании Федерального 
            закона от 29.07.1998 № 135-ФЗ «Об оценочной деятельности в Российской Федерации» и не могут быть использованы в качестве официального документа 
            при разрешении споров в суде, представлении в государственные органы или кредитные организации.
          </p>
          <p className="mt-2">
            2.2. Отдельные автоматизированные сервисы Сайта используют алгоритмы машинного обучения и искусственного интеллекта (AI/ML) для первичного анализа 
            и структурирования вводных данных. Сгенерированные алгоритмами заключения являются синтетическими аналитическими суждениями, носят вспомогательный 
            характер и подлежат обязательной верификации профильным M&A-аналитиком Компании в рамках проведения процедуры предпродажного аудита (Vendor Due Diligence, VDD). 
            Компания не несет ответственности за любые прямые или косвенные убытки, возникшие в результате использования автоматизированных расчетов Сайта.
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">СТАТЬЯ 3. ЗАЩИТА АЛГОРИТМОВ И ЗАПРЕТ AI-СКРЕЙПИНГА</strong>
          <p>
            Все математические формулы, коэффициенты дисконтирования рисков, тексты закрытых исследований и исходный код терминала являются охраняемой 
            интеллектуальной собственностью Компании. Запрещен автоматический парсинг данных и использование контента платформы для дообучения сторонних моделей без письменного согласия.
          </p>
        </div>
      </div>
    )
  },

  'privacy-protocol': {
    title: 'TRADE SECRET & DATA LOCALIZATION • 98-ФЗ • 152-ФЗ • VDR STANDARDS',
    content: (
      <div className="space-y-6 text-[13px] leading-relaxed font-light text-[#141416]/70">
        <div className="text-[#141416] pb-3 border-b border-[#EAE5DE]">
          <strong className="text-[#8C6D37] block text-sm font-mono uppercase mb-1 font-bold">
            ПОЛОЖЕНИЕ О РЕЖИМЕ КОММЕРЧЕСКОЙ ТАЙНЫ, BLIND-ТИЗЕРАХ И ЛОКАЛИЗАЦИИ ДАННЫХ
          </strong>
          <span className="font-mono text-[11px] text-[#141416]/50 block">
            Нормативное обеспечение конфиденциальности по Федеральным законам № 98-ФЗ и № 152-ФЗ
          </span>
        </div>
        
        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            СТАТЬЯ 1. РЕЖИМ КОММЕРЧЕСКОЙ ТАЙНЫ И СИСТЕМА BLIND TEASER (ФЗ № 98-ФЗ)
          </strong>
          <p>
            1.1. Компания обеспечивает режим конфиденциальности сведений о продавцах, покупателях и параметрах объектов сделок в соответствии с требованиями 
            Федерального закона от 29.07.2004 № 98-ФЗ «О коммерческой тайне».
          </p>
          <p className="mt-2">
            1.2. Первичная информация о продаваемых медицинских и стоматологических активах публично размещается на Сайте исключительно в форме обезличенных 
            информационных материалов («Blind Teasers»). Опубликованные показатели не содержат фирменного наименования, коммерческого обозначения (бренда), 
            точного адреса местонахождения и иных данных, позволяющих идентифицировать правообладателя актива. Допускается раскрытие масштаба (количество кресел/коек), 
            федерального округа и агрегированных финансовых показателей без раскрытия наименования юрлица и персональных данных учредителей.
          </p>
          <p className="mt-2">
            1.3. Предоставление расширенного информационного меморандума (Information Memorandum), доступа к виртуальной комнате данных (Virtual Data Room, VDR) 
            и раскрытие идентифицирующих сведений об объекте осуществляются строго после прохождения процедуры верификации статуса покупателя (KYC) и подписания 
            между Сторонами соглашения о конфиденциальности (Non-Disclosure Agreement, NDA).
          </p>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            СТАТЬЯ 2. ТЕХНОЛОГИЧЕСКИЕ ПРОТОКОЛЫ VIRTUAL DATA ROOM (VDR)
          </strong>
          <p>
            2.1. Динамическое водяное маркирование (Dynamic Watermarking): каждый открываемый в VDR документ динамически маркируется штампом с указанием 
            IP-адреса, корпоративного email, точного времени сессии и уникального ID проверяющего аудитора.
          </p>
          <p className="mt-2">
            2.2. Цифровое управление правами (DRM): запрет печати, скачивания исходных файлов, снятия скриншотов и копирования текста в буфер обмена.
          </p>
          <p className="mt-2">
            2.3. Детализированный аудит сессий (Detailed Audit Trail): посекундная фиксация просмотров каждого листа и раздела VDR с сохранением журналов в неизменяемом хранилище.
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">
            СТАТЬЯ 3. ЛОКАЛИЗАЦИЯ СЕРВЕРОВ И РЕГЛАМЕНТ 24/72 ЧАСА (ФЗ № 152-ФЗ)
          </strong>
          <p>
            3.1. Обработка данных осуществляется в распределенной облачной инфраструктуре. Передача данных защищена криптографическими протоколами (TLS 1.3 / AES-256), обеспечивающими защиту от перехвата (MITM).
          </p>
          <p className="mt-2">
            3.2. Оператор своевременно направляет уведомление об обработке персональных данных в Роскомнадзор в порядке ст. 22 ФЗ № 152-ФЗ.
          </p>
          <p className="mt-2">
            3.3. В случае любого инцидента безопасности с персональными данными Оператор незамедлительно уведомляет Роскомнадзор в течение 24 часов с момента обнаружения и в течение 72 часов предоставляет отчет о результатах внутреннего расследования (ч. 3.1 ст. 21 ФЗ № 152-ФЗ).
          </p>
        </div>
      </div>
    )
  },

  'cookie-policy': {
    title: 'COOKIE & WEB ANALYTICS COMPLIANCE • ФЗ № 152-ФЗ',
    content: (
      <div className="space-y-6 text-[13px] leading-relaxed font-light text-[#141416]/70">
        <div className="text-[#141416] pb-3 border-b border-[#EAE5DE]">
          <strong className="text-[#8C6D37] block text-sm font-mono uppercase mb-1 font-bold">
            ПОЛОЖЕНИЕ ОБ ИСПОЛЬЗОВАНИИ ФАЙЛОВ COOKIE И РАБОТЕ ВЕБ-АНАЛИТИКИ
          </strong>
          <span className="font-mono text-[11px] text-[#141416]/50 block">
            Соответствие Федеральному закону от 27.07.2006 № 152-ФЗ «О персональных данных»
          </span>
        </div>
        
        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">1. НАЗНАЧЕНИЕ И КАТЕГОРИИ ФАЙЛОВ COOKIE</strong>
          <p>
            1.1. Сайт использует файлы cookie и сетевые идентификаторы (включая client-id сервиса «Яндекс Метрика», счетчик № 112544173) 
            для обеспечения защищенного соединения, авторизации в контуре VDR, анализа стабильности калькуляторов и совершенствования пользовательского опыта.
          </p>
          <p className="mt-2">
            1.2. Категории файлов cookie:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-1 text-[#141416]/80">
            <li><strong>Технические (необходимые):</strong> поддержка сессии TLS 1.3, предотвращение CSRF-атак, сохранение выбранных правовых настроек. Данные cookie не могут быть отключены.</li>
            <li><strong>Аналитические (Яндекс Метрика):</strong> деперсонифицированный учет взаимодействия с разделами платформы и расчетными моделями. Включаются только при согласии пользователя.</li>
            <li><strong>Маркетинговые и сессионные:</strong> учет источников перехода и параметров UTM для отслеживания институциональных обращений.</li>
          </ul>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <strong className="text-[#8C6D37] block font-mono text-xs uppercase mb-1.5 font-bold">
            2. УПРАВЛЕНИЕ СОГЛАСИЕМ И ОТЗЫВ
          </strong>
          <p>
            2.1. Пользователь управляет согласием через интерактивную панель управления файлами cookie (Cookie Consent Manager). 
            Выбор пользователя фиксируется в системе с указанием временной метки.
          </p>
          <p className="mt-2">
            2.2. Пользователь может в любой момент изменить или отозвать согласие на обработку файлов cookie, вызвав окно настроек через ссылку в подвале Сайта.
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">3. ЛОКАЛИЗАЦИЯ И ТРАНСГРАНИЧНАЯ ПЕРЕДАЧА</strong>
          <p>
            3.1. Инфраструктура платформы развернута на базе глобальных облачных провайдеров, обеспечивающих защиту от DDoS-атак и шифрование трафика. Возможна трансграничная передача данных в юрисдикции, обеспечивающие адекватную защиту персональных данных, в целях обеспечения работоспособности сервисов.
          </p>
        </div>
      </div>
    )
  },

  'arbitration-clauses': {
    title: 'INSTITUTIONAL ARBITRATION CLAUSES • МКАС ТПП РФ & РАЦ (2026)',
    content: (
      <div className="space-y-6 text-[13px] leading-relaxed font-light text-[#141416]/70">
        <div className="text-[#141416] pb-3 border-b border-[#EAE5DE]">
          <strong className="text-[#8C6D37] block text-sm font-mono uppercase mb-1 font-bold">
            РЕКОМЕНДОВАННЫЕ АРБИТРАЖНЫЕ ОГОВОРКИ ДЛЯ ДОГОВОРОВ И NDA
          </strong>
          <span className="font-mono text-[11px] text-[#141416]/50 block">
            Институциональный третейский арбитраж: Международный коммерческий арбитражный суд при ТПП РФ и Российский Арбитражный Центр (РАЦ)
          </span>
        </div>
        
        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <div className="flex items-center gap-2 mb-2">
            <Landmark className="w-4 h-4 text-[#8C6D37]" />
            <strong className="text-[#8C6D37] font-mono text-xs uppercase font-bold">
              ВАРИАНТ 1. МКАС ПРИ ТОРГОВО-ПРОМЫШЛЕННОЙ ПАЛАТЕ РФ (РЕКОМЕНДОВАННЫЙ)
            </strong>
          </div>
          <p className="font-mono text-xs text-[#141416]/80 bg-[#FAF8F5] p-4 rounded leading-relaxed border border-[#EAE5DE] select-all">
            «Все споры, разногласия или требования, возникающие из настоящего Соглашения, Соглашений о конфиденциальности (NDA), 
            Договоров об оказании консультационных услуг или в связи с ними, в том числе касающиеся их исполнения, нарушения, прекращения 
            или недействительности, подлежат передаче на разрешение в Международный коммерческий арбитражный суд при Торгово-промышленной палате 
            Российской Федерации в соответствии с его применимыми регламентами. Арбитражное разбирательство проводится в г. Москве на русском языке. 
            Решение арбитража является окончательным и обязательным для обеих Сторон.»
          </p>
        </div>

        <div className="p-4 bg-white border border-[#EAE5DE] rounded-[3px] shadow-2xs">
          <div className="flex items-center gap-2 mb-2">
            <Scale className="w-4 h-4 text-[#8C6D37]" />
            <strong className="text-[#8C6D37] font-mono text-xs uppercase font-bold">
              ВАРИАНТ 2. РОССИЙСКИЙ АРБИТРАЖНЫЙ ЦЕНТР (РАЦ) ПРИ АНО «ИНСТИТУТ СОВРЕМЕННОГО АРБИТРАЖА»
            </strong>
          </div>
          <p className="font-mono text-xs text-[#141416]/80 bg-[#FAF8F5] p-4 rounded leading-relaxed border border-[#EAE5DE] select-all">
            «Все споры, возникающие из настоящего Договора (Соглашения) или в связи с ним, в том числе касающиеся его заключения, изменения, 
            исполнения, нарушения, расторжения или недействительности, подлежат разрешению в арбитраже (третейском разбирательстве), 
            администрируемом Российским Арбитражным Центром при АНО «Институт современного арбитража» в соответствии с Правилами арбитража. 
            Местом арбитража является г. Москва. Язык арбитражного разбирательства — русский. Решение арбитража является окончательным для Сторон.»
          </p>
        </div>

        <div>
          <strong className="text-[#141416] block font-mono text-xs uppercase mb-1.5">ПРЕИМУЩЕСТВА ТРЕТЕЙСКОГО АРБИТРАЖА ДЛЯ M&A В ЗДРАВООХРАНЕНИИ</strong>
          <p>
            • Полная конфиденциальность: материалы дел и решения арбитража не публикуются в открытых базах данных (в отличие от картотеки арбитражных дел КАД Арбитр).<br/>
            • Скорость разрешения споров: средний срок рассмотрения составляет 2–3 месяца без многолетних апелляций и кассаций.<br/>
            • Отраслевая экспертиза арбитров: привлечение арбитров со специализацией в корпоративном праве, M&A и медицинском регулировании.
          </p>
        </div>
      </div>
    )
  }
};

export const LegalOverlay: React.FC<LegalOverlayProps> = ({ docType, onClose }) => {
  useEffect(() => {
    if (docType) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [docType]);

  const doc = docType ? DOCUMENTS[docType] : null;

  return (
    <AnimatePresence>
      {docType && doc && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
        >
          {/* Backdrop with strong blur and tint to match Dark Terminal theme */}
          <div 
            className="absolute inset-0 bg-[#0B0B0C]/85 backdrop-blur-md"
            onClick={onClose}
          />
          
          <motion.div 
            initial={{ y: 20, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", bounce: 0, duration: 0.4 }}
            className="relative w-full max-w-4xl max-h-[88vh] bg-[#FAF8F5] border border-[#EAE5DE] shadow-2xl rounded-[4px] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 sm:p-8 border-b border-[#EAE5DE] bg-white">
              <div className="flex items-center gap-3">
                <ShieldAlert className="w-5 h-5 text-[#8C6D37]" />
                <h3 className="text-sm sm:text-base font-mono font-medium text-[#141416] uppercase tracking-wider">
                  {doc.title}
                </h3>
              </div>
              <button 
                onClick={onClose}
                className="text-[#141416]/50 hover:text-[#141416] transition-colors p-1 cursor-pointer"
                aria-label="Закрыть"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="p-6 sm:p-8 overflow-y-auto scrollbar-thin scrollbar-thumb-[#EAE5DE] scrollbar-track-transparent">
              {doc.content}
            </div>

            {/* Footer with quick action */}
            <div className="p-4 bg-[#F5F2EB] border-t border-[#EAE5DE] flex items-center justify-between">
              <span className="font-mono text-[11px] text-[#141416]/60">
                Structure & Partners M&A Advisory • Legal & Compliance Office
              </span>
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-[#8C6D37] hover:bg-[#785D2E] text-white font-mono text-xs uppercase tracking-wider rounded-[2px] transition-colors cursor-pointer font-semibold shadow-xs"
              >
                Закрыть документ
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
